import type { CheckReport, FixMode, MarkdownDocxStats, Profile, RuleInfo, TitlePageInfo } from '@gostword/engine';

export interface MarkdownRequest {
  text: string;
  /** false — без содержания; не задано — если в работе больше трёх разделов. */
  toc?: boolean;
  numberSections: boolean;
  /** Данные титульного листа; false — без титульного листа. */
  titlePage?: TitlePageInfo | false;
}

export interface MarkdownResponse {
  file: ArrayBuffer;
  warnings: string[];
  stats: MarkdownDocxStats;
  title?: string;
}

export interface FixRequest {
  mode: FixMode;
  comments: boolean;
  exclude: string[];
}

export interface FixResponse {
  file: ArrayBuffer;
  fixed: number;
  commented: number;
  failed: number;
  /** Как пользователь получил файл: скачал или получил в чат с ботом в Telegram. */
  delivered?: 'downloaded' | 'sent';
}

export interface CheckResponse {
  report: CheckReport;
  /** Сетевые запросы, сделанные обработчиком во время проверки (должно быть 0). */
  requests: number;
}

export class EngineError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

interface Pending {
  resolve: (value: never) => void;
  reject: (error: Error) => void;
}

/** Клиент движка: вся обработка идёт в Web Worker, интерфейс не подвисает на больших файлах. */
export class EngineClient {
  private readonly worker: Worker;
  private seq = 0;
  private readonly pending = new Map<number, Pending>();

  constructor() {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent) => {
      const { id, ok, error, code, ...rest } = e.data as { id: number; ok: boolean; error?: string; code?: string };
      const p = this.pending.get(id);
      if (!p) return;
      this.pending.delete(id);
      if (ok) p.resolve(rest as never);
      else p.reject(new EngineError(error ?? 'Неизвестная ошибка', code));
    };
    this.worker.onerror = (e) => {
      for (const p of this.pending.values()) p.reject(new EngineError(`Сбой обработчика: ${e.message}`));
      this.pending.clear();
    };
  }

  private call<T>(message: object, transfer: Transferable[] = []): Promise<T> {
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: never) => void, reject });
      this.worker.postMessage({ ...message, id }, transfer);
    });
  }

  check(file: ArrayBuffer, name: string, profile: Profile): Promise<CheckResponse> {
    return this.call({ type: 'check', file, name, profile }, [file]);
  }

  fix(request: FixRequest, profile: Profile): Promise<FixResponse> {
    return this.call({ type: 'fix', ...request, profile });
  }

  /** Документ по ГОСТу из текста в Markdown. */
  markdown(request: MarkdownRequest, profile: Profile): Promise<MarkdownResponse> {
    return this.call({ type: 'markdown', ...request, profile });
  }

  /** Список правил для настроек. */
  async rules(): Promise<RuleInfo[]> {
    return (await this.call<{ rules: RuleInfo[] }>({ type: 'rules' })).rules;
  }
}
